import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sms } from '@repletoryreach/sdk-api';
import { Stack, Card, Title, Text, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Switch } from '@repletoryreach/sdk-ui/inputs';
import { notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { useSmsStatus } from '../../../lib/sms';

/**
 * Text messages (SMS), 4 October 2026.
 *
 * One switch for now: a WhatsApp service message or code that cannot be
 * delivered because the number has no WhatsApp goes as an SMS instead, charged
 * per part from credits. Marketing never falls back. Off by default.
 *
 * Owners and admins only (the nav marks it so, and the service refuses an
 * agent). The page does not exist while the platform has no SMS provider: an
 * old link lands back on the settings hub.
 */
export default function SettingsSmsPage() {
  const t = useT();
  const qc = useQueryClient();
  const smsStatus = useSmsStatus();

  const { data, isPending, error } = useQuery({
    queryKey: ['sms-settings'],
    queryFn: () => sms.getSettings(),
    enabled: smsStatus.available
  });

  const [fallback, setFallback] = useState(false);
  useEffect(() => {
    if (data?.data) setFallback(data.data.fallback_enabled);
  }, [data]);

  // The switch saves itself and reverts visibly if the write fails, the same
  // as the notification switches.
  const save = useMutation({
    mutationFn: (next: boolean) => sms.updateSettings({ fallback_enabled: next }),
    onMutate: (next) => {
      const previous = fallback;
      setFallback(next);
      return { previous };
    },
    onError: (e, _next, ctx) => {
      if (ctx) setFallback(ctx.previous);
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.nav.sms'));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['sms-settings'] });
      // The status carries the same flag, and other pages read it.
      void qc.invalidateQueries({ queryKey: ['sms-status'] });
      void qc.invalidateQueries({ queryKey: ['sms', 'status'] });
    }
  });

  if (smsStatus.isPending) return <Skeleton height={160} />;
  if (!smsStatus.available) return <Navigate to="/settings" replace />;

  return (
    <Stack gap="md">
      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('settings.sms.fallback.title')}</Title>

          {error && <Alert color="error" role="alert">{t('settings.sms.loadError')}</Alert>}

          {/* Nothing is interactive until the stored value is in hand, so the
              first paint never claims "off" over a saved "on". */}
          {isPending && !error && <Skeleton height={64} />}

          {!isPending && !error && (
            <Switch
              label={t('settings.sms.fallback.label')}
              description={t('settings.sms.fallback.hint')}
              checked={fallback}
              onChange={(next: boolean) => save.mutate(next)}
              disabled={save.isPending}
            />
          )}

          {!isPending && !error && (
            <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.sms.autosave')}</Text>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
